import { Field, RawData } from '../../types';
import { useRsi } from '../../hooks/useRsi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setColumn } from '../MatchColumns/setColumn';
import { setSubColumn } from '../MatchColumns/setSubColumn';
import { findUnmatchedRequiredFields } from '../MatchColumns/findUnmatchedRequiredFields';
import { normalizeTableData } from '../MatchColumns/normalizeTableData';
import { getMatchedColumns } from '../MatchColumns/getMatchedColumns';
import { ColumnCard } from '../ColumnCard';
import { setIgnoreColumn } from '../MatchColumns/setIgnoreColumn';
import Button from 'common/components/button/Button';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { DeepReadonly } from 'ts-essentials';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { getFieldOptionAlternates, getFieldOptions } from '../MatchColumns/getFieldOptions';
import { date_formats } from 'Config';
import Utils from 'common/services/Utils';
import mappingStyles from '../ColumnMapping.module.scss';

export type MatchColumnsProps<T extends string> = {
  data: RawData[];
  headerValues: RawData;
  matchedColumns?: Columns<T>;
  onContinue: (data: any[], rawData: RawData[], columns: Columns<T>) => void;
  onPrevious: (data: any[], rawData: RawData[], columns: Columns<T>) => Promise<void>;
};

export enum ColumnType {
  empty,
  ignored,
  matched,
  matchedCheckbox,
  matchedSelect,
  matchedSelectOptions,
}

export type MatchedOptions<T> = { entry: string; value: T; isFuelType: boolean, dateFormat?: string };

type EmptyColumn = { type: ColumnType.empty; index: number; header: string };
type IgnoredColumn = { type: ColumnType.ignored; index: number; header: string };
export type MatchedColumn<T> = { type: ColumnType.matched; index: number; header: string; value: T, matchedOptions?: Partial<MatchedOptions<T>>[]; };
type MatchedSwitchColumn<T> = { type: ColumnType.matchedCheckbox; index: number; header: string; value: T };
export type MatchedSelectColumn<T> = {
  type: ColumnType.matchedSelect;
  index: number;
  header: string;
  value: T;
  matchedOptions: Partial<MatchedOptions<T>>[];
  dateFormat?: T;
};
export type MatchedSelectOptionsColumn<T> = {
  type: ColumnType.matchedSelectOptions;
  index: number;
  header: string;
  value: T;
  matchedOptions: MatchedOptions<T>[];
};

export type Column<T extends string> =
  | EmptyColumn
  | IgnoredColumn
  | MatchedColumn<T>
  | MatchedSwitchColumn<T>
  | MatchedSelectColumn<T>
  | MatchedSelectOptionsColumn<T>;

export type Columns<T extends string> = Column<T>[];

export const MatchColumnsStep = <T extends string>({ data, headerValues, matchedColumns, onContinue, onPrevious }: MatchColumnsProps<T>) => {
  const dataExample = data.slice(0, 2);
  const { fields, onClose } = useRsi<T>();
  const [isLoading, setIsLoading] = useState(false);
  const [isMatchLoading, setMatchIsLoading] = useState(false);

  const [columns, setColumns] = useState<Columns<T>>(
    ([...headerValues] as string[]).map((value, index) => ({ type: ColumnType.empty, index, header: value ?? '' })),
  );
  const { addToast } = useToasts();
  const warningToast = useCallback((description: string) => { addToast(description, { appearance: 'warning' }) }, []);
  const [selectedOptions, setSelectedOptions] = useState<{ value: DeepReadonly<T>; label: string; }[]>([]);

  const isMounted = useRef(true);

  useEffect(() => {
    const matchColumns = async () => {
      setMatchIsLoading(true);

      const redraw = matchedColumns && !!matchedColumns.length ? false : true;

      const __matchedColumns = matchedColumns && !!matchedColumns.length ? matchedColumns : await getMatchedColumns(columns, fields, data, 1);

      if (redraw) {
        __matchedColumns.forEach(column => {
          fillColumnsOptions(column);
        });
      }


      if (isMounted.current || redraw) {
        setColumns(__matchedColumns);
      }

      setMatchIsLoading(false);

    };

    matchColumns();

    return () => {
      isMounted.current = false;
    };
  }, []);


  const fillColumnsOptions = (column: Column<T>) => {
    if (column && 'matchedOptions' in column && column.matchedOptions && column.matchedOptions.length) {

      const field = fields.find(f => f && ('value' in column && column.value === f.key));
      if (field) {

        const options = getFieldOptions(fields, column.value);

        column.matchedOptions = column.matchedOptions.filter((x, i) => true)
          .map((option, i) => {

            if (!field.onlyOneOption || !!field.onlyOneOption && !!option.entry && i === 0) {

              const alternates = getFieldOptionAlternates(fields, column.value);

              let value: any | null = null;
              let alternateMatch: any | null = null;

              if (column.value == 'date' || column.value === 'paymentDate') {

                const formats: string[] = [];
                for (const format of date_formats) {
                  const date = Utils.parseToDate(option.entry, format);
                  if (date) {
                    formats.push(format)
                  }
                }

                if (formats.length == 1) {
                  alternateMatch = alternates.find((opt: any) => opt.source == formats[0]);
                  value = options.find((opt: any) => opt.value == formats[0]);
                }

              }
              else if (column.value == 'registration') {

                const limparString = (texto?: string) => {
                  return (texto ?? '').replace(/[^a-zA-Z0-9]/g, '')
                }

                alternateMatch = alternates.find((opt: any) => limparString(opt.source) == limparString(option.entry));
                value = options.find((opt: any) => limparString(opt.value) == limparString(option.value));

              }
              else if (column.value == 'responsible') {

                const normalizeString = (text?: string) => {
                  return (text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9\s]/g, '').toLowerCase().trim();
                }

                alternateMatch = alternates.find((opt: any) => normalizeString(opt.source) == normalizeString(option.entry));
                value = options.find((opt: any) => normalizeString(opt.label) == normalizeString(option.entry));

              }
              else {

                alternateMatch = alternates.find((opt: any) => opt.source == option.entry);
                value = options.find((opt: any) => opt.value == option.value ?? '');

              }

              const alternate = options.find((opt: any) => opt.value == (alternateMatch ? alternateMatch?.target : ''));

              const selectedOption = value ?? alternate;
              if (selectedOption) {
                return { ...option, value: selectedOption.value, isFuelType: selectedOption.isFuelType }
              }

            }

            return option;

          })

        if (column.matchedOptions?.every(({ value }) => !!value)) {
          column.type = ColumnType.matchedSelectOptions;
        }

      }
    }
    return column;
  }

  useEffect(() => {
    setSelectedOptions(fields
      .filter(f => f && !(columns.findIndex(column => 'value' in column && column.value === f.key) < 0))
      .map(({ label, key }) => ({ value: key, label })));
  }, [columns]);

  const [showUnmatchedFieldsAlert, setShowUnmatchedFieldsAlert] = useState(false);
  const { t } = useTranslation();
  const onChange = useCallback(
    (value: T, columnIndex: number) => {
      const field = value ? fields.find(field => field.key === value) as unknown as Field<T> : undefined;
      const existingFieldIndex = field && columns.findIndex(column => 'value' in column && column.value === field.key);
      const cols = columns.map<Column<T>>((column, index) => {
        if (columnIndex === index) {
          return fillColumnsOptions(setColumn(column, field, data));
        } else if (index === existingFieldIndex) {
          warningToast(t('spreadsheet_import.match_columns_step.duplicate_column_warning_description'))
          return fillColumnsOptions(setColumn(column));
        } else {
          return column;
        }
      });

      setColumns(cols);
    },
    [
      columns,
      data,
      fields,
      `${t('spreadsheet_import.match_columns_step.duplicate_column_warning_description')}`
    ],
  );

  const onIgnore = useCallback(
    (columnIndex: number) => {
      setColumns(columns.map((column, index) => (columnIndex === index ? setIgnoreColumn<T>(column) : column)));
    },
    [columns, setColumns],
  );

  const onRevertIgnore = useCallback(
    (columnIndex: number) => {
      setColumns(columns.map((column, index) => (columnIndex === index ? fillColumnsOptions(setColumn(column)) : column)));
    },
    [columns, setColumns],
  );

  const onSubChange = useCallback(
    (value: string, isFuelType: boolean, columnIndex: number, entry: string) => {

      setColumns(
        columns.map((column, index) => {

          let result = column;

          if (columnIndex === index && 'matchedOptions' in column) {
            const field = value ? fields.find(field => field.key === column.value) as unknown as Field<T> : undefined;
            result = setSubColumn(column, entry, value, isFuelType, field?.onlyOneOption);
          }

          return result;
        }),
      );
    },
    [columns, setColumns],
  );
  const unmatchedRequiredFields = useMemo(() => findUnmatchedRequiredFields(fields, columns), [fields, columns]);

  const requiredFields = useMemo(() => fields.filter(f => f.validations?.some(v => v.rule === 'required')), [fields]);
  const requiredTotal = requiredFields.length;
  const requiredMapped = useMemo(
    () => requiredFields.filter(field => columns.some(col => 'value' in col && col.value === field.key)).length,
    [requiredFields, columns],
  );
  const ignoredCount = useMemo(() => columns.filter(col => col.type === ColumnType.ignored).length, [columns]);

  const handleOnContinue = useCallback(async () => {
    if (unmatchedRequiredFields.length > 0) {
      setShowUnmatchedFieldsAlert(true);
      warningToast(t('spreadsheet_import.alerts.unmatched_required_fields.header_title'));
    } else {
      setIsLoading(true);

      columns.forEach(column => {
        if ('matchedOptions' in column && column.matchedOptions && column.matchedOptions.length > 0) {
          const field = fields.find(field => field.key === column.value)!;
          if (field.onlyOneOption) {
            const auxValue: string | undefined = column.matchedOptions.find(x => !!x.value)?.value;
            column.matchedOptions.forEach(mo => {
              mo.value = (auxValue ?? '') as T;
            });
          }
        }
      });

      const normalizedData = normalizeTableData(columns, data, fields);

      onContinue(normalizedData, data, columns);
      setIsLoading(false);
    }
  }, [unmatchedRequiredFields.length, onContinue, columns, data, fields]);

  const handleOnPrevious = useCallback(async () => {
    setIsLoading(true);
    const normalizedData = normalizeTableData(columns, data, fields);
    onPrevious(normalizedData, data, columns);
    setIsLoading(false);
  }, [onPrevious, columns, data, fields]);

  return (
    <>
      <div className="spreadsheet-step-scroll" style={{ flex: 1, overflow: 'auto' }}>
        {isMatchLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <b>
              {t('spreadsheet_import.match_columns_step.user_table_title')}
              {' '}
              <span className={mappingStyles.count}>
                {columns.length} {t('spreadsheet_import.match_columns_step.columns_count')}
              </span>
            </b>
            <p style={{ fontSize: '0.8rem' }}>
              {t('spreadsheet_import.match_columns_step.info_message')}
            </p>
            <div className={mappingStyles.colList}>
              {columns.map(column => (
                <ColumnCard
                  key={column.header + column.index}
                  column={column}
                  dataExample={dataExample}
                  fields={fields}
                  selectedOptions={selectedOptions}
                  onChange={onChange}
                  onSubChange={onSubChange}
                  onIgnore={onIgnore}
                  onRevertIgnore={onRevertIgnore}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {isLoading ? (
        <></>
      ) : (
        <div className={mappingStyles.footerRow}>
          <div className={mappingStyles.footerInfo}>
            <strong>{requiredMapped}</strong> {t('spreadsheet_import.match_columns_step.of_label')} <strong>{requiredTotal}</strong> {t('spreadsheet_import.match_columns_step.required_mapped')}
            {ignoredCount > 0 && (
              <span className={mappingStyles.ignoredCount}>
                {ignoredCount} {t('spreadsheet_import.match_columns_step.ignored_count')}
              </span>
            )}
          </div>
          <div>
            <Button preset={'secondary'} type="button" onClick={() => onClose()} text={t('common.cancel')} />
            <Button type="button" onClick={() => handleOnPrevious()} text={t('spreadsheet_import.select_header_step.previous_button_title')} />
            <Button onClick={handleOnContinue}>
              {t('spreadsheet_import.match_columns_step.next_button_title')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
